import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Icons } from "@/components/icons";

export default function Overview() {
  // Demo data for coordinator
  const stats = {
    totalCoordinated: 285400,
    activeCollectors: 12,
    thisMonth: 89650,
    approvedToday: 15,
  };

  const recentActivities = [
    {
      id: "ACT001",
      type: "approval",
      description: "Approved payment HAF025 by Hassan Al-Mahmoud",
      amount: 3200,
      timestamp: "1 hour ago",
      status: "completed",
    },
    {
      id: "ACT002",
      type: "assignment",
      description: "Assigned collector Ahmed Al-Rashid to West Bay area",
      amount: null,
      timestamp: "3 hours ago",
      status: "active",
    },
    {
      id: "ACT003",
      type: "review",
      description: "Reviewed and flagged payment HAF028 for verification",
      amount: 5500,
      timestamp: "5 hours ago",
      status: "pending",
    },
    {
      id: "ACT004",
      type: "approval",
      description: "Approved payment HAF024 by Fatima Hassan",
      amount: 2800,
      timestamp: "1 day ago",
      status: "completed",
    },
  ];

  const collectorPerformance = [
    {
      name: "Hassan Al-Mahmoud",
      area: "Doha Central",
      collected: 45200,
      payments: 18,
      status: "active",
    },
    {
      name: "Ahmed Al-Rashid",
      area: "West Bay",
      collected: 38900,
      payments: 15,
      status: "active",
    },
    {
      name: "Fatima Hassan",
      area: "Al Rayyan",
      collected: 29800,
      payments: 12,
      status: "inactive",
    },
    {
      name: "Omar Abdullah",
      area: "Lusail",
      collected: 52100,
      payments: 21,
      status: "active",
    },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case "approval":
        return <Icons.checkCircle className="h-4 w-4 text-green-600" />;
      case "assignment":
        return <Icons.user className="h-4 w-4 text-blue-600" />;
      case "review":
        return <Icons.eye className="h-4 w-4 text-orange-600" />;
      default:
        return <Icons.fileText className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">
          Coordinator Dashboard
        </h2>
        <p className="text-muted-foreground">
          Welcome to your HAF Coordinator dashboard - manage collectors and
          oversee operations
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Coordinated
            </CardTitle>
            <Icons.dollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              QR {stats.totalCoordinated.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              +18% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Collectors
            </CardTitle>
            <Icons.users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeCollectors}</div>
            <p className="text-xs text-muted-foreground">2 new this week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <Icons.calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              QR {stats.thisMonth.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              +15% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Approved Today
            </CardTitle>
            <Icons.checkCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.approvedToday}</div>
            <p className="text-xs text-muted-foreground">Payments processed</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Activities */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activities</CardTitle>
            <CardDescription>
              Latest coordination activities and approvals
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 border-b pb-3 last:border-b-0"
                >
                  <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium">
                      {activity.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{activity.timestamp}</span>
                      {activity.amount && (
                        <>
                          <span>•</span>
                          <span>QR {activity.amount.toLocaleString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant={
                      activity.status === "completed"
                        ? "default"
                        : activity.status === "pending"
                        ? "destructive"
                        : "secondary"
                    }
                    className="text-xs"
                  >
                    {activity.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Collector Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Collector Performance</CardTitle>
            <CardDescription>
              Overview of your team&apos;s collection activities
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {collectorPerformance.map((collector, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between border-b pb-3 last:border-b-0"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{collector.name}</span>
                      <Badge
                        variant={
                          collector.status === "active"
                            ? "default"
                            : "secondary"
                        }
                        className="text-xs"
                      >
                        {collector.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {collector.area}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">
                      QR {collector.collected.toLocaleString()}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {collector.payments} payments
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common coordination tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="flex flex-col items-center space-y-2 rounded-lg border p-4 hover:bg-accent cursor-pointer">
              <Icons.checkCircle className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Approve Payments</span>
            </div>
            <div className="flex flex-col items-center space-y-2 rounded-lg border p-4 hover:bg-accent cursor-pointer">
              <Icons.user className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Manage Collectors</span>
            </div>
            <div className="flex flex-col items-center space-y-2 rounded-lg border p-4 hover:bg-accent cursor-pointer">
              <Icons.search className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Review Payments</span>
            </div>
            <div className="flex flex-col items-center space-y-2 rounded-lg border p-4 hover:bg-accent cursor-pointer">
              <Icons.fileText className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Generate Reports</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
